/**
 * OC Chat Panel Component
 * Wrapper around ChatPanel that handles rendering based on authentication state
 * Separated from OcFabWidget for better organization
 */

import { Alert } from '@hl/ui-components';
import { FC } from 'react';
import { ThemeProvider } from 'styled-components';

import { IAppTheme } from '../../../../styles/IAppTheme';
import { OcChatConfig } from '../../../../types/oc';
import ChatBackgroundSVG from '../../../BannerSVG/ChatBackgroundSVG';
import { ChatPanel } from '../../../ChatPanel/ChatPanel';
import { LoadingEllipsis } from '../../../Message/LoadingEllipsis';
import { ErrorWrapper } from '../../../TranscriptBox/TranscriptMessages.styled';
import { isAuthError } from '../../config/ocDefaults';
import { useOcFabWidgetOptions } from '../../contexts/OcFabWidgetOptionsContext';
import { useAiAssistantEndpoints, useMockedAiAssistantEndpoints } from '../../hooks/useAiAssistantEndpoints';
import { useSessionManagerContext } from '../../sessionManagement/SessionManagerContext';
import { OcLoginRequired } from '../LoginRequired/OcLoginRequired';
import { OcChatProviders } from '../Providers/OcChatProviders';
import WithVerticalSources from '../Sources/WithVerticalSources';
import ChatInnerWithHistory from './ChatInnerWithHistory';
import ChatInnerWithoutHistory from './ChatInnerWithoutHistory';
import { LoadingContainer } from './ChatPanelContent.styled';
import PanelChatHeader from './PanelChatHeader';

/** Reusable loading spinner themed to match the chat panel. */
const PanelLoadingContent: FC<{ theme: IAppTheme }> = ({ theme }) => {
  const loadingTheme = { ...theme, color: { ...theme.color, loadingEllipses: theme.color.chatPanel } };
  return (
    <ThemeProvider theme={loadingTheme}>
      <LoadingContainer>
        <LoadingEllipsis />
      </LoadingContainer>
    </ThemeProvider>
  );
};

/** Reusable error alert for panel-level failures. */
const PanelErrorContent: FC<{ message?: string }> = ({ message }) => (
  <ErrorWrapper>
    {/* @ts-expect-error HL components is on an older react version where children is implicitly included */}
    <Alert danger={true}>
      {message || 'An error occurred. Reloading the page may fix the issue.'}
    </Alert>
  </ErrorWrapper>
);

/** Panel shell when privacy is not success yet (loading or error). Used so we do not mount SessionManager until privacy has succeeded. */
export const OcChatPanelLoadingShell: FC<{
  theme: IAppTheme;
  isOpen: boolean;
  onClose: () => void;
  error?: Error | null;
}> = ({ theme, isOpen, onClose, error }) => {
  const { panelBaseWidth, panelMinWidth, panelMaxWidth } = useOcFabWidgetOptions();
  const commonPanelProps = {
    show: isOpen,
    onHide: onClose,
    initialWidth: Math.min(panelBaseWidth, window.document.body.getBoundingClientRect().width),
    minWidth: panelMinWidth,
    maxWidth: panelMaxWidth,
  };
  return (
    <ChatPanel className="hl-oc-chat-content" {...commonPanelProps}>
      {error ? <PanelErrorContent message={error.message} /> : <PanelLoadingContent theme={theme} />}
    </ChatPanel>
  );
};

export type AuthState =
  | 'checking'
  | 'authenticated'
  | 'unauthenticated'
  | 'error';

export interface OcChatPanelProps {
  config: OcChatConfig;
  userId: string;
  isPublicAnonymousUser?: boolean;
  authState: AuthState;
  error: Error | null;
  theme: IAppTheme;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * OcChatPanel Component
 *
 * Renders the appropriate content for the chat panel based on authentication state:
 * - Loading skeleton while checking authentication
 * - Login prompt for unauthenticated users
 * - Error message for non-auth errors
 * - Full chat interface for authenticated users
 */
export const OcChatPanel: FC<OcChatPanelProps> = ({
  config,
  userId,
  isPublicAnonymousUser = false,
  authState,
  error,
  theme,
  isOpen,
  onClose,
}) => {
  const {
    previewState,
    useSVGBackground,
    panelBaseWidth,
    panelMinWidth,
    panelMaxWidth,
  } = useOcFabWidgetOptions();

  // Call hooks at top level (before any conditional returns)
  // showChatHistory from session manager reflects Crest privacy (isPrivate/isAnonymous).
  // When backend says private or anonymous, effectiveShowChatHistory is false and we hide the history panel.
  const { renameSessionIfNeeded, initialSessionKey, chatContextRef, showChatHistory } =
    useSessionManagerContext();

  // Use mocked endpoints in preview mode, otherwise use production endpoints
  const isPreviewMode = config.mode === 'preview';
  // Always call both hooks unconditionally to satisfy React hooks rules
  const productionEndpoints = useAiAssistantEndpoints(config, renameSessionIfNeeded);
  const mockedEndpoints = useMockedAiAssistantEndpoints(previewState, renameSessionIfNeeded);

  const endpoints = isPreviewMode ? mockedEndpoints : productionEndpoints;

  const appConfig = {
    systemId: config.aiAssistantKey,
    tenantId: config.tenantId,
    userId,
    isPrivate: config.isPrivate,
    isAnonymous: config.isAnonymous,
    isPublicAnonymousUser,
  };

  const commonPanelProps = {
    show: isOpen,
    onHide: onClose,
    initialWidth: Math.min(
      panelBaseWidth,
      window.document.body.getBoundingClientRect().width,
    ),
    minWidth: panelMinWidth,
    maxWidth: panelMaxWidth,
  };

  if (authState === 'checking') {
    return (
      <ChatPanel className="hl-oc-chat-content" {...commonPanelProps}>
        <PanelLoadingContent theme={theme} />
      </ChatPanel>
    );
  }

  // Render login required
  if (
    authState === 'unauthenticated' ||
    (authState === 'error' && error && isAuthError(error))
  ) {
    return (
      <ChatPanel className="hl-oc-chat-content" {...commonPanelProps}>
        <OcLoginRequired
          loginUrl={config.loginUrl}
        />
      </ChatPanel>
    );
  }

  if (authState === 'error' && error) {
    return (
      <ChatPanel className="hl-oc-chat-content" {...commonPanelProps}>
        <PanelErrorContent />
      </ChatPanel>
    );
  }

  // Authenticated - render full chat interface
  return (
    <ChatPanel
      className="hl-oc-chat-content"
      {...commonPanelProps}
      customBackground={useSVGBackground && <ChatBackgroundSVG />}
    >
      <OcChatProviders
        endpoints={endpoints}
        appConfig={appConfig}
        config={config}
        theme={theme}
        initialSessionKey={initialSessionKey ?? undefined}
        chatContextRef={chatContextRef}
      >
        <WithVerticalSources>
          <PanelChatHeader />
          {showChatHistory && <ChatInnerWithHistory />}
          {!showChatHistory && <ChatInnerWithoutHistory />}
        </WithVerticalSources>
      </OcChatProviders>
    </ChatPanel>
  );
};

export default OcChatPanel;
